import { Button, Select, TextInput, Textarea, Image } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState, useEffect, useMemo } from 'react';
import { issueTypeToDisplay } from './IssueTypeButton';
import IssueTable from './IssueTable';
import { api } from '~/trpc-extra/api';
import { useProjectId } from '~/hooks/context/useProjectId';
import { notifications } from '@mantine/notifications';
import type { GetTestRunAttemptType } from '~/server/api/routers/testRun';
import { LinearCreateIssueView } from '~/components/linear/LinearCreateIssueView';
import dedent from 'dedent';

// TODO: support create an issue without a run
// TODO: add in attemptId
export default function FindIssueDialog({
  runId,
  runAttemptId,
  closeDialog,
}: {
  runId: string;
  runAttemptId: string;
  closeDialog: () => void;
}) {
  const projectId = useProjectId();
  const trpcUtils = api.useUtils();

  const [saving, setSaving] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [showLinearView, setShowLinearView] = useState(false);

  const { data: hasLinearInstalled } = api.project.hasLinearInstalled.useQuery({ projectId: projectId || '' });

  const createErrorDetails = ({
    testName,
    testRunAttempts,
  }: {
    testName: string;
    testRunAttempts: GetTestRunAttemptType[];
  }) => {
    const attemptStats =
      testRunAttempts.length > 0 ?
        {
          totalAttempts: testRunAttempts.length,
          failedAttempts: testRunAttempts.filter((attempt) => attempt.status === 'FAILED').length,
        }
      : undefined;

    const mostRecentError = [...testRunAttempts]
      .reverse()
      .find((attempt) => attempt.executionOutput?.errors && attempt.executionOutput.errors.length > 0)?.executionOutput
      ?.errors?.[0];

    return {
      testName,
      errorMessage: mostRecentError?.error ?? '',
      stepTitle: mostRecentError?.stepDescription ?? '',
      attemptStats: attemptStats ?? { totalAttempts: 0, failedAttempts: 0 },
    };
  };

  const { data: testRun } = api.testRun.get.useQuery(
    { runId },
    {
      enabled: !!runId,
    },
  );

  const errorDetails = useMemo(
    () =>
      createErrorDetails({
        testName: testRun?.test.name || '',
        testRunAttempts: testRun?.TestRunAttempts || [],
      }),
    [testRun?.test.name, testRun?.TestRunAttempts],
  );

  const createIssue = api.issues.create.useMutation({
    onSuccess: () => {
      trpcUtils.issues.list.invalidate().catch(() => undefined);
      trpcUtils.testRun.getIssues.invalidate().catch(() => undefined);
      trpcUtils.suiteRun.getIssues.invalidate().catch(() => undefined);
      notifications.show({
        message: 'Issue created',
        color: 'green',
      });
      closeDialog();
    },
  });
  const connectIssues = api.issues.connectTestRunToIssues.useMutation({
    onSuccess: () => {
      trpcUtils.testRun.getIssues.invalidate().catch(() => undefined);
      trpcUtils.testRun.getIssues.invalidate().catch(() => undefined);
      trpcUtils.suiteRun.getIssues.invalidate().catch(() => undefined);
      notifications.show({
        message: 'Connected to issues',
        color: 'green',
      });
      closeDialog();
    },
  });

  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      issueType: 'OTHER',
    },
    validate: (values) => {
      const errors: { title?: string } = {};
      if (values.title.trim() === '') {
        errors.title = 'Name is required';
      }
      return errors;
    },
  });

  useEffect(() => {
    // Populate the form *only* when the query has returned meaningful data and the user hasn't touched the form yet.
    if (!form.isDirty() && errorDetails.testName) {
      const defaultDescription =
        errorDetails.errorMessage ? `@ step "${errorDetails.stepTitle}"\n${errorDetails.errorMessage}` : '';

      let defaultIssueType: 'BUG' | 'FLAKE' | 'NEEDS_REWRITE' | 'OTHER' = 'OTHER';

      if (
        errorDetails.attemptStats.failedAttempts === errorDetails.attemptStats.totalAttempts &&
        errorDetails.attemptStats.totalAttempts > 0
      ) {
        // All attempts failed, default to bug
        defaultIssueType = 'BUG';
      }

      form.setValues({
        title: errorDetails.testName,
        description: defaultDescription,
        issueType: defaultIssueType,
      });
    }
  }, [errorDetails]);

  if (showLinearView) {
    return (
      <div className="p-6">
        <LinearCreateIssueView
          projectId={projectId || ''}
          onBack={() => setShowLinearView(false)}
          onSuccess={() => {
            setShowLinearView(false);
            closeDialog();
          }}
          prefilledDetails={
            errorDetails ?
              {
                title: `Stably Test${errorDetails.errorMessage ? ' Failed' : ''}: ${errorDetails.testName}`,
                description:
                  errorDetails.errorMessage ?
                    dedent`The following error occurred at step "${errorDetails.stepTitle}":
                        ${errorDetails.errorMessage}
                        ---
                        View the detailed test run at: ${typeof window !== 'undefined' ? window.location.href : ''}`
                  : '',
              }
            : undefined
          }
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Content Section */}
      <div className="flex items-center justify-between space-x-4">
        <div className="text-lg font-bold">{isCreatingNew ? 'Create a new issue' : 'Connect to an existing issue'}</div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setIsCreatingNew((o) => !o)}
          >
            {isCreatingNew ? 'Connect Existing' : 'Create New'}
          </Button>
          {!!hasLinearInstalled && (
            <Button
              variant="filled"
              color="gray"
              onClick={() => setShowLinearView(true)}
              leftSection={
                <Image
                  src="/assets/linear-light-logo.svg"
                  alt="Linear logo"
                  className="h-4 w-4"
                />
              }
            >
              + Linear Issue
            </Button>
          )}
        </div>
      </div>

      {!isCreatingNew && (
        <IssueTable
          primaryAction="connect"
          primaryActionFunc={async (issueIds) => {
            await connectIssues.mutateAsync({
              testRunAttemptId: runAttemptId,
              issueIds,
            });
          }}
        />
      )}

      <div className={isCreatingNew ? '' : 'hidden'}>
        <form
          className="flex flex-col space-y-2"
          onSubmit={form.onSubmit(async (values) => {
            if (saving) {
              return;
            }
            setSaving(true);
            await createIssue.mutateAsync({
              title: values.title,
              description: values.description,
              // TODO:
              testRunAttemptId: runAttemptId,
              projectId: projectId || '',
              issueType: values.issueType,
            });
            setSaving(false);
            form.resetDirty();
          })}
        >
          <TextInput
            label="Title"
            required
            {...form.getInputProps('title')}
          />
          <Textarea
            label="Description"
            {...form.getInputProps('description')}
          />
          <Select
            label="Issue Type"
            required
            placeholder="Pick an issue type"
            comboboxProps={{
              zIndex: 350,
            }}
            {...form.getInputProps('issueType')}
            data={(['BUG', 'FLAKE', 'NEEDS_REWRITE', 'OTHER'] as const).map((ip) => ({
              value: ip,
              label: issueTypeToDisplay(ip),
            }))}
          />

          <div className="mt-4 flex justify-end">
            <Button
              type="submit"
              disabled={saving}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
